import React, { useEffect, useMemo, useState } from "react";
import { ChevronRight, KeyRound, MapPin, Phone } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { cn } from "../../../../utils/cn";
import { useScheduleDetailAppointments } from "../ScheduleDetailPanel/useScheduleDetailAppointments";
import {
  formatHourlyWeatherLine,
  findHourlyWeatherForSchedule,
  formatNextVisitEyebrow,
  type NextVisitCandidate,
} from "../../mobileConsultantAgendaModel";
import type { CityWeather } from "../../agendaWeatherTypes";
import {
  formatVisitPropertyAddress,
  formatVisitPropertyCompactAddress,
} from "../../../../utils/visitDetailsProperty";
import { resolveVisitKeyLocationName } from "../../../../utils/visitKeyLocation";
import { buildWhatsAppLink, normalizePhoneDigits } from "../../../../utils/whatsappLink";
import { weatherConditionIcon } from "../../weatherConditionUi";
import { normalizeWeatherHourKey } from "../../agendaWeatherHourKey";

type NextVisitCardProps = {
  visit: NextVisitCandidate;
  selectedDateIso: string;
  weatherCityName: string;
  cityWeather?: CityWeather | null;
  clientLabel: string;
  /** Opens the same visit details as tapping the visit in the list. */
  onOpenDetails?: () => void;
};

/**
 * Three actions share the card width; "WhatsApp" is the longest label, so its column is
 * wider. Up to 480px the icon goes above the label: the text never wraps nor gets cut.
 */
const ACTION_GRID =
  "mt-4 grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_minmax(0,1fr)] gap-2";
const ACTION_BUTTON =
  "inline-flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-lg px-2 text-sm font-semibold max-[480px]:flex-col max-[480px]:gap-1 max-[480px]:py-2 max-[480px]:text-xs";
const ACTION_LABEL = "whitespace-nowrap leading-tight";

function buildGoogleMapsRouteUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

const NextVisitCard: React.FC<NextVisitCardProps> = ({
  visit,
  selectedDateIso,
  weatherCityName,
  cityWeather,
  clientLabel,
  onOpenDetails,
}) => {
  const [eyebrowLabel, setEyebrowLabel] = useState(() =>
    formatNextVisitEyebrow(selectedDateIso, visit.schedule.date)
  );

  useEffect(() => {
    const sync = () => {
      setEyebrowLabel(
        formatNextVisitEyebrow(selectedDateIso, visit.schedule.date)
      );
    };
    sync();
    const timer = window.setInterval(sync, 60_000);
    return () => window.clearInterval(timer);
  }, [selectedDateIso, visit.schedule.date]);

  const { appointments, isFetching } = useScheduleDetailAppointments({
    open: true,
    scheduleParam: visit.coord,
    targetScheduleId: visit.schedule.id,
  });

  const appointment = useMemo(() => {
    return (
      appointments.find((item) => item.id === visit.schedule.id) ??
      appointments[0] ??
      null
    );
  }, [appointments, visit.schedule.id]);

  const property = appointment?.property ?? null;
  const client = appointment?.client as Record<string, unknown> | null;
  const phone =
    (client?.phone as string | undefined) ??
    (client?.cellphone as string | undefined) ??
    null;

  // Several clients in the same hour (same property): list each one with own contacts.
  const isMultiVisit = appointments.length > 1;
  const slotClients = useMemo(
    () =>
      appointments.map((item) => {
        const data = item.client as Record<string, unknown> | null;
        const clientPhone =
          (data?.phone as string | undefined) ?? (data?.cellphone as string | undefined) ?? null;
        return {
          id: item.id,
          name: data?.first_name ? String(data.first_name) : "Cliente",
          whatsappLink: buildWhatsAppLink(clientPhone),
          telDigits: normalizePhoneDigits(clientPhone),
        };
      }),
    [appointments]
  );

  const timeLabel =
    visit.schedule.date.replace("T", " ").split(" ")[1]?.slice(0, 5) ?? "";
  const titleNames =
    clientLabel ||
    (client?.first_name ? String(client.first_name) : "Cliente");

  const propertyLine = useMemo(() => {
    if (!property) {
      return null;
    }
    const code = property.code ? String(property.code) : property.id ? `#${property.id}` : "";
    const compactAddress = formatVisitPropertyCompactAddress(property);
    return [code, compactAddress].filter(Boolean).join(" · ");
  }, [property]);

  const keyLocation = appointment
    ? resolveVisitKeyLocationName(property, appointment.key_location)
    : null;

  const hourlyWeather = useMemo(() => {
    if (
      !cityWeather?.hours?.length
    ) {
      return null;
    }
    const fromSchedule = findHourlyWeatherForSchedule({
      cityName: weatherCityName,
      scheduleDate: visit.schedule.date,
      hours: cityWeather.hours,
    });
    if (fromSchedule) {
      return fromSchedule;
    }

    const hourKey = normalizeWeatherHourKey(timeLabel);
    return (
      cityWeather.hours.find(
        (entry) => normalizeWeatherHourKey(entry.time) === hourKey
      ) ?? null
    );
  }, [cityWeather, timeLabel, weatherCityName, visit.schedule]);

  const weatherLine = formatHourlyWeatherLine(hourlyWeather, timeLabel);
  const WeatherIcon = hourlyWeather
    ? weatherConditionIcon(hourlyWeather.condition)
    : null;

  const address = property ? formatVisitPropertyAddress(property) : "";
  const whatsappLink = buildWhatsAppLink(phone);
  const telDigits = normalizePhoneDigits(phone);

  // Route, WhatsApp and call keep their own action instead of opening the details.
  const stopCardClick = (event: React.SyntheticEvent) => event.stopPropagation();

  return (
    <article
      role={onOpenDetails ? "button" : undefined}
      tabIndex={onOpenDetails ? 0 : undefined}
      aria-label={onOpenDetails ? `Ver detalhes da visita das ${timeLabel}` : undefined}
      onClick={onOpenDetails}
      onKeyDown={
        onOpenDetails
          ? (event) => {
              if (event.target !== event.currentTarget) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onOpenDetails();
              }
            }
          : undefined
      }
      className={cn(
        "mb-5 rounded-[18px] bg-[#2D3748] p-4 text-white shadow-md",
        onOpenDetails &&
          "cursor-pointer transition active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FBD38D]/70"
      )}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#FBD38D]">
          {eyebrowLabel}
        </p>
        <div className="flex shrink-0 items-center gap-1.5">
          {isMultiVisit ? (
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold">
              {appointments.length} clientes
            </span>
          ) : null}
          {onOpenDetails ? (
            <ChevronRight className="h-5 w-5 text-white/60" aria-hidden />
          ) : null}
        </div>
      </div>

      <p className="text-[22px] font-semibold leading-snug">
        {timeLabel} · {isMultiVisit ? `${appointments.length} visitas` : titleNames}
      </p>

      {isFetching && !propertyLine ? (
        <p className="mt-2 text-sm text-white/70">Carregando imóvel...</p>
      ) : null}

      {propertyLine ? (
        <p className="mt-2 text-sm text-white/90">{propertyLine}</p>
      ) : null}

      {appointment ? (
        <p className="mt-1 flex items-center gap-1.5 text-sm text-white/80">
          <KeyRound className="h-4 w-4 shrink-0" aria-hidden />
          Chaves: {keyLocation && keyLocation !== "—" ? keyLocation : "não informada"}
        </p>
      ) : null}

      {weatherLine && WeatherIcon ? (
        <p className="mt-2 flex items-start gap-1.5 text-sm leading-5 text-[#FBD38D]">
          <WeatherIcon className="h-4 w-4 shrink-0 text-[#FBD38D]" aria-hidden />
          {weatherLine}
        </p>
      ) : null}

      {isMultiVisit ? (
        <ul
          className="m-0 mt-4 flex list-none snap-x gap-2 overflow-x-auto p-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          aria-label="Clientes desta visita"
        >
          {slotClients.map((slotClient) => (
            <li
              key={slotClient.id}
              className="flex w-[150px] shrink-0 snap-start flex-col gap-2 rounded-xl bg-white/10 p-2.5"
            >
              <span className="truncate text-sm font-semibold">{slotClient.name}</span>
              <div className="grid grid-cols-2 gap-1.5">
                {slotClient.whatsappLink ? (
                  <a
                    href={slotClient.whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={stopCardClick}
                    aria-label={`WhatsApp de ${slotClient.name}`}
                    className="inline-flex min-h-9 items-center justify-center rounded-lg bg-[#25D366] text-white"
                  >
                    <FaWhatsapp className="h-4 w-4" aria-hidden />
                  </a>
                ) : (
                  <span className="min-h-9 rounded-lg bg-white/10" aria-hidden />
                )}
                {slotClient.telDigits ? (
                  <a
                    href={`tel:${slotClient.telDigits}`}
                    onClick={stopCardClick}
                    aria-label={`Ligar para ${slotClient.name}`}
                    className="inline-flex min-h-9 items-center justify-center rounded-lg bg-[#4A5568] text-white"
                  >
                    <Phone className="h-4 w-4" aria-hidden />
                  </a>
                ) : (
                  <span className="min-h-9 rounded-lg bg-white/10" aria-hidden />
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {isMultiVisit ? (
        // Contacts are per client above; the route is the same property for everyone.
        address ? (
          <a
            href={buildGoogleMapsRouteUrl(address)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stopCardClick}
            className={cn(ACTION_BUTTON, "mt-3 w-full bg-white text-[#2D3748] hover:bg-white/90")}
          >
            <MapPin className="h-4 w-4 shrink-0" aria-hidden />
            <span className={ACTION_LABEL}>Rota até o imóvel</span>
          </a>
        ) : null
      ) : (
      <div className={ACTION_GRID}>
        {address ? (
          <a
            href={buildGoogleMapsRouteUrl(address)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stopCardClick}
            className={cn(ACTION_BUTTON, "bg-white text-[#2D3748] hover:bg-white/90")}
          >
            <MapPin className="h-4 w-4 shrink-0" aria-hidden />
            <span className={ACTION_LABEL}>Rota</span>
          </a>
        ) : (
          <span className="min-h-11 rounded-lg bg-white/10" aria-hidden />
        )}
        {whatsappLink ? (
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stopCardClick}
            className={cn(ACTION_BUTTON, "bg-[#25D366] text-white")}
          >
            <FaWhatsapp className="h-4 w-4 shrink-0" aria-hidden />
            <span className={ACTION_LABEL}>WhatsApp</span>
          </a>
        ) : (
          <span className="min-h-11 rounded-lg bg-white/10" aria-hidden />
        )}
        {telDigits ? (
          <a
            href={`tel:${telDigits}`}
            onClick={stopCardClick}
            className={cn(ACTION_BUTTON, "bg-[#4A5568] text-white hover:bg-[#3d4654]")}
          >
            <Phone className="h-4 w-4 shrink-0" aria-hidden />
            <span className={ACTION_LABEL}>Ligar</span>
          </a>
        ) : (
          <span className="min-h-11 rounded-lg bg-white/10" aria-hidden />
        )}
      </div>
      )}
    </article>
  );
};

export default NextVisitCard;
